(function () {
  'use strict';

  const measurement = window.FwdMeasurementContract;
  if (!measurement) return;
  const bridge = window.FwdMeasurementBridge;

  const CONFIG_ID = 'FwdAnalyticsConfig';
  const EXCLUSION_KEY = 'fwd:analytics:excluded';
  const IDENTITY_KEY = 'fwd:analytics:identified-customer';
  const MAX_QUEUE_SIZE = 100;
  const MAX_QUEUE_AGE_MS = 10000;
  const SDK_LOAD_TIMEOUT_MS = 6000;
  const LOCATION_URL = 'https://fwd-naver-shopping-ep.jeonchiweon.workers.dev/analytics/location';
  const LOCATION_TIMEOUT_MS = 5000;
  const PIXEL_READY_KEY = 'fwd:analytics:pixel-ready';
  const PRIVACY_BOOTSTRAP_TIMEOUT_MS = 6000;
  const PRIVACY_BOOTSTRAP_RETRY_MS = 200;
  const MAX_PRIVACY_LOAD_ATTEMPTS = 3;
  const SESSION_KEY = 'fwd:analytics:session';
  const NAVIGATION_KEY = 'fwd:analytics:navigation';
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  const NAVIGATION_TTL_MS = 30 * 1000;
  const MAX_ENGAGEMENT_SEGMENT_MS = 30 * 60 * 1000;
  const SCROLL_CHECKPOINTS = [25, 50, 75, 100];
  const AUTOCAPTURE_BLOCK_SELECTORS = [
    '.mp-sensitive',
    '.mp-block',
    '[data-fwd-analytics-block]',
    'input[type="password"]',
    'input[name*="email" i]',
    'input[name*="phone" i]',
    'input[name*="address" i]',
    'form[action*="/account"]',
    'form[action*="/checkout"]',
  ];
  const AUTOMATIC_SENSITIVE_PROPERTIES = [
    '$city', '$latitude', '$longitude', '$ip', 'ip', 'city', 'latitude', 'longitude',
    '$current_url',
    '$referrer',
    '$referring_domain',
    '$initial_referrer',
    '$initial_referring_domain',
    '$search_engine',
    'mp_keyword',
    'current_url_search',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
    'utm_source_platform',
    'utm_campaign_id',
    'utm_creative_format',
    'utm_marketing_tactic',
    'dclid',
    'fbclid',
    'gclid',
    'ko_click_id',
    'li_fat_id',
    'msclkid',
    'sccid',
    'ttclid',
    'twclid',
    'wbraid',
  ];
  const MIXPANEL_RESIDENCIES = {
    eu: { apiHost: 'https://api-eu.mixpanel.com', dataLocation: 'European Union' },
    india: { apiHost: 'https://api-in.mixpanel.com', dataLocation: 'India' },
    us: { apiHost: 'https://api-js.mixpanel.com', dataLocation: 'United States' },
  };
  const MIXPANEL_BATCH_QUEUE_SUFFIXES = ['_ev', '_pp', '_gr'];
  const MAX_ACTIVE_FILTERS = 20;
  const MAX_CATALOG_LIST_VALUES = 10;
  const FILTER_PARAMS = new Set([
    'filter.p.m.custom.brand',
    'filter.p.vendor',
    'filter.p.m.custom.size',
    'filter.p.m.custom.size_recommend', 'filter.p.m.custom.ai_size_recommend',
    'filter.p.m.custom.fwd_recommended_size',
    'filter.v.option.size',
    'filter.v.option.사이즈',
    'filter.v.availability',
    'filter.p.tag',
    'filter.p.m.custom.decades',
    'filter.p.m.custom.pants_fit',
    'filter.v.price.gte',
    'filter.v.price.lte',
  ]);

  const COMMON_PROPERTIES = [
    'mp_country_code', '$region',
    'supports_product_click', 'supports_wishlist', 'supports_filters', 'supports_components', 'supports_journeys', 'supports_exposures',
    'list_render_id', 'filter_state_id', 'source_list_render_id', 'source_filter_state_id', 'tab_id',
    'analytics_schema_version', 'analytics_collector', 'product_category_source', 'price_unit', 'page_view_id', 'source_page_view_id',
    'previous_page_path', 'previous_page_type', 'navigation_status', 'navigation_action',
    'navigation_interaction_id', 'component_key', 'component_label', 'component_role',
    'session_id',
    'page_url',
    'page_path',
    'page_type',
    'page_title',
    'previous_page_url',
    'navigation_event',
    'navigation_element_key',
    'navigation_element_role',
    'navigation_element_label',
    'referrer',
    'interaction_id',
    'action',
    'result',
    'placement',
    'component_name',
    'component_type',
    'collection_handle',
    'active_filters',
    'active_filter_count',
    'active_filters_truncated',
    'brand_id',
    'brand_handle',
    'brand_url',
    'navigation_source_product_id',
    'navigation_source_variant_id',
    'navigation_source_product_url',
    'navigation_source_list_name',
    'navigation_source_list_position',
    'navigation_source_placement',
    'navigation_source_collection_handle',
    'navigation_source_active_filters',
    'navigation_source_active_filter_count',
    'navigation_source_active_filters_truncated',
  ];

  const PRODUCT_PROPERTIES = [
    'product_id', 'variant_id', 'product_name', 'product_url', 'variant_title',
    'brand', 'brand_id', 'brand_handle', 'brand_url', 'size', 'tagged_sizes',
    'recommended_sizes', 'price', 'currency', 'product_category',
  ];

  const EVENT_PROPERTIES = {
    'Session Started': [],
    'Page Viewed': PRODUCT_PROPERTIES.concat(['list_name', 'list_position']),
    'Page Hidden': [],
    'Page Engagement': PRODUCT_PROPERTIES.concat(['visible_duration_ms', 'engagement_segment_id', 'lifecycle_cause', 'visible_duration_capped', '$insert_id', 'segment_started_at', 'segment_ended_at', 'measurement_clock', 'segment_clock_discontinuity']),
    'Page Visible': ['hidden_duration_ms'],
    'Session Exit Attempted': [],
    'Link Clicked': ['destination_url'],
    'UI Interaction': PRODUCT_PROPERTIES,
    'Page Scrolled': ['scroll_depth'],
    'Product Viewed': PRODUCT_PROPERTIES,
    'Product Clicked': PRODUCT_PROPERTIES.concat(['list_name', 'list_position']),
    'Wishlist Item Added': [
      'product_id', 'variant_id', 'product_name', 'brand', 'price', 'currency', 'product_category',
      'list_name', 'list_position', 'wishlist_count',
    ],
    'Wishlist Item Removed': [
      'product_id', 'variant_id', 'product_name', 'brand', 'price', 'currency', 'product_category',
      'list_name', 'list_position', 'wishlist_count',
    ],
    'Wishlist Cleared': ['wishlist_count'],
    'Wishlist Added to Cart': [
      'product_id', 'variant_id', 'mode', 'candidate_count', 'requested_count',
      'skipped_count', 'added_count', 'failure_stage', 'cart_request_id',
    ],
    'Cart Item Added': PRODUCT_PROPERTIES.concat([
      'cart_request_id', 'quantity', 'cart_quantity',
    ]),
    'Search Opened': ['surface'],
    'Search Closed': ['surface'],
    'Search Query Changed': ['surface', 'search_query', 'query_length'],
    'Search Submitted': ['surface', 'search_query', 'query_length'],
    'Search Result Selected': [
      'surface', 'search_query', 'query_length', 'product_id', 'variant_id',
      'product_name', 'brand', 'price', 'currency', 'product_category', 'list_name', 'list_position',
    ],
    'Product Filter Changed': [
      'filter_group', 'filter_param', 'filter_value', 'filter_value_id',
      'change_type', 'result_count',
    ],
    'Product Sort Changed': ['sort_value', 'result_count'],
    'Product Exposed': PRODUCT_PROPERTIES.concat(['list_name', 'list_position']),
    'Product Filters Applied': ['request_id', 'action_id', 'previous_filter_state_id', 'before_filter_tokens', 'after_filter_tokens', 'change_type', 'applied', 'result_count'],
    'Newsletter Signup Completed': ['surface', 'result'],
    'Product Inquiry Opened': ['product_id', 'variant_id', 'destination'],
  };

  // Only observed actions/navigation renew inactivity. Measurement and lifecycle
  // observations stay associated with the current session without extending it.
  const SESSION_ACTIVITY_EVENTS = new Set([
    'Page Viewed', 'Link Clicked', 'UI Interaction', 'Page Scrolled', 'Product Clicked',
    'Wishlist Item Added', 'Wishlist Item Removed', 'Wishlist Cleared', 'Wishlist Added to Cart',
    'Cart Item Added', 'Search Opened', 'Search Closed', 'Search Query Changed', 'Search Submitted',
    'Search Result Selected', 'Product Filter Changed', 'Product Sort Changed', 'Product Filters Applied',
    'Newsletter Signup Completed', 'Product Inquiry Opened',
  ]);

  const NUMERIC_PROPERTIES = new Set([
    'analytics_schema_version',
    'list_position',
    'wishlist_count',
    'candidate_count',
    'requested_count',
    'skipped_count',
    'added_count',
    'query_length',
    'result_count',
    'scroll_depth',
    'visible_duration_ms',
    'hidden_duration_ms',
    'active_filter_count',
    'navigation_source_active_filter_count',
    'quantity',
    'cart_quantity',
    'navigation_source_list_position',
  ]);

  const BOOLEAN_PROPERTIES = new Set(['visible_duration_capped', 'active_filters_truncated', 'navigation_source_active_filters_truncated']);
  const URL_PROPERTIES = new Set([
    'page_url', 'previous_page_url', 'product_url', 'brand_url', 'referrer',
    'navigation_source_product_url', 'destination_url',
  ]);
  const LIST_PROPERTIES = new Set(['tagged_sizes', 'recommended_sizes']);

  const config = readConfig();
  const state = {
    excluded: Boolean(config.excluded) || readExclusion(),
    identifiedCustomerId: null,
    identityLoaded: false,
    instance: null,
    privacy: 'unknown',
    geography: {},
    geographyGeneration: 0,
    geographyRequested: false,
    geographyController: null,
    geographyTimer: null,
    queue: [],
    coreLoadGeneration: 0,
    coreScript: null,
    sdkTimer: null,
    sdk: 'idle',
    pixelReadyNonce: null,
    wasOptedOut: false,
    behaviorBound: false,
    handledClicks: new WeakSet(),
    hiddenAt: null,
    identityFailed: false,
    lastVisibleAt: Date.now(),
    lifecycleGeneration: 0,
    navigation: null,
    pageViewId: null,
    engagementStartedAt: null,
    navigationFailure: null,
    pageViewed: false,
    scrollCheckpoints: new Set(),
    scrollScheduled: false,
    sessionId: null,
    sessionLastActivityAt: 0,
    sessionLoaded: false,
    catalog: config.page.catalog,
    productViewed: false,
    privacyBootstrapStartedAt: null,
    privacyBootstrapTimer: null,
    privacyLoadAttempts: 0,
    privacyLoadGeneration: 0,
    sequence: 0,
    listRenderId: null,
    filterStateId: null,
    engagementTimer: null,
    engagementStartedWall: null,
    exposureObserver: null,
    exposureTimers: new Map(),
    exposures: new Set(),
    qa: false,
    tabId: null,
  };

  const api = {
    excludeCurrentBrowser,
    includeCurrentBrowser,
    status,
    setCatalogContext,
    setProductContext,
    track,
  };

  window.fwdAnalytics = api;
  bindPrivacyChanges();
  startPrivacyGate();

  function readConfig() {
    try {
      const node = document.getElementById(CONFIG_ID);
      const parsed = node ? JSON.parse(node.textContent || '{}') : {};
      return {
        compliance: normalizeCompliance(parsed.compliance),
        customer: normalizeCustomer(parsed.customer),
        coreUrl: normalizeCoreUrl(parsed.coreUrl),
        excluded: parsed.excluded === true,
        page: normalizePage(parsed.page),
        token: typeof parsed.token === 'string' ? parsed.token.trim() : '',
        measurementMode: parsed.measurementMode === 'pixel' ? 'pixel' : 'legacy',
        collectorRevision: normalizeIdentityValue(parsed.collectorRevision, 80) || 'unversioned',
      };
    } catch (_error) {
      return { compliance: null, customer: null, coreUrl: '', excluded: false, page: {}, token: '' };
    }
  }

  function normalizePage(value) {
    const page = value && typeof value === 'object' ? value : {};
    return {
      catalog: normalizeCatalogContext(page.catalog),
      title: normalizeIdentityValue(page.title, 256),
      type: normalizeIdentityValue(page.type, 64),
    };
  }

  function normalizeCatalogContext(value) {
    const source = value && typeof value === 'object' ? value : {};
    const activeFilters = normalizeActiveFilters(source.activeFilters);
    const brand = normalizeBrandContext(source.brand);
    const projection = measurement.projectFilters(source.activeFilters, { truncated: source.activeFiltersTruncated });
    return {
      active_filters: activeFilters,
      active_filter_count: activeFilters.length,
      active_filters_truncated: source.activeFiltersTruncated === true || (Array.isArray(source.activeFilters) && source.activeFilters.length > MAX_ACTIVE_FILTERS),
      brand,
      collection_handle: normalizeHandle(source.collectionHandle),
      product: normalizeProductContext(source.product),
      filterInputStatus: projection.filter_state_status,
    };
  }

  function normalizeProductContext(value) {
    if (!value || typeof value !== 'object') return null;
    const result = {
      brand: normalizeIdentityValue(value.brand, 120),
      brand_handle: normalizeHandle(value.brandHandle),
      brand_id: normalizeMetaobjectId(value.brandId),
      brand_url: normalizeSafeUrl(value.brandUrl),
      currency: normalizeCurrency(value.currency),
      price: value.price,
      product_id: normalizeShopifyId(String(value.productId || ''), 'Product'),
      product_name: normalizeIdentityValue(value.productName, 256),
      product_category: safePublicLabel(value.productCategory),
      product_url: normalizeSafeUrl(value.productUrl),
      recommended_sizes: normalizeStringList(value.recommendedSizes),
      size: normalizeIdentityValue(value.size, 100),
      tagged_sizes: normalizeStringList(value.taggedSizes),
      variant_id: normalizeShopifyId(String(value.variantId || ''), 'ProductVariant'),
      variant_title: normalizeIdentityValue(value.variantTitle, 160),
    };
    return compactObject(result);
  }

  function normalizeBrandContext(value) {
    if (!value || typeof value !== 'object') return null;
    return compactObject({
      brand: normalizeIdentityValue(value.brand, 120),
      brand_handle: normalizeHandle(value.brandHandle),
      brand_id: normalizeMetaobjectId(value.brandId),
      brand_url: normalizeSafeUrl(value.brandUrl),
    });
  }

  function normalizeActiveFilters(value) {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    const result = [];
    value.slice(0, MAX_ACTIVE_FILTERS).forEach(function (entry) {
      if (!entry || typeof entry !== 'object') return;
      const param = normalizeIdentityValue(entry.param, 80);
      const normalizedValue = normalizeFilterValue(param, entry.value);
      const label = normalizeIdentityValue(entry.label, 100);
      if (!param || !FILTER_PARAMS.has(param) || normalizedValue === undefined) return;
      const key = param + '\u0000' + normalizedValue;
      if (seen.has(key)) return;
      seen.add(key);
      result.push({ param, value: normalizedValue, label: label && !containsSensitivePattern(label) ? label : '이름 미확인 [' + normalizedValue + ']' });
    });
    return result;
  }

  function normalizeFilterValue(param, value) {
    if (!param || !FILTER_PARAMS.has(param)) return undefined;
    if (param === 'filter.v.price.gte' || param === 'filter.v.price.lte') {
      const number = Number(value);
      if (!Number.isFinite(number) || number < 0 || number > 1000000000000) return undefined;
      const decimal = Math.round(number * 100) / 100;
      return Math.abs(number - decimal) < Number.EPSILON * Math.max(1, number) ? decimal : undefined;
    }
    const normalized = normalizeIdentityValue(value, 180);
    if (!normalized || containsSensitivePattern(normalized)) return undefined;
    if (param === 'filter.v.availability' && !['0', '1'].includes(normalized)) return undefined;
    if (param === 'filter.p.tag' && !['new', 'store-only', 'coming-soon'].includes(normalized)) return undefined;
    if (param.startsWith('filter.p.m.custom.') && !/^gid:\/\/shopify\/Metaobject\/\d+$/.test(normalized)) return undefined;
    return normalized;
  }

  function containsSensitivePattern(value) {
    return /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(value)
      || /(?:\+?82[- .]?)?0?1[016789][- .]?\d{3,4}[- .]?\d{4}/.test(value)
      || /\b(?:order|주문)[- _:#]*[A-Z0-9]{5,}\b/i.test(value);
  }

  function normalizeStringList(value) {
    if (!Array.isArray(value)) return undefined;
    const seen = new Set();
    const result = [];
    value.slice(0, MAX_CATALOG_LIST_VALUES).forEach(function (entry) {
      const normalized = normalizeIdentityValue(entry, 100);
      if (!normalized || containsSensitivePattern(normalized) || seen.has(normalized)) return;
      seen.add(normalized);
      result.push(normalized);
    });
    return result.length ? result : undefined;
  }

  function normalizeHandle(value) {
    const normalized = normalizeIdentityValue(value, 100);
    return normalized && /^[a-z0-9][a-z0-9-]*$/.test(normalized) ? normalized : undefined;
  }

  function normalizeMetaobjectId(value) {
    if (value === null || value === undefined) return undefined;
    const normalized = String(value).trim();
    if (/^\d+$/.test(normalized)) return 'gid://shopify/Metaobject/' + normalized;
    return /^gid:\/\/shopify\/Metaobject\/\d+$/.test(normalized) ? normalized : undefined;
  }

  function normalizeCurrency(value) {
    const normalized = normalizeIdentityValue(value, 3);
    return normalized && /^[A-Z]{3}$/.test(normalized.toUpperCase()) ? normalized.toUpperCase() : undefined;
  }

  function normalizeFiniteInteger(value, minimum) {
    const number = Number(value);
    return Number.isFinite(number) && number >= minimum ? Math.round(number) : undefined;
  }

  function compactObject(value) {
    const result = {};
    Object.keys(value).forEach(function (key) {
      if (value[key] !== undefined && value[key] !== null && value[key] !== '') result[key] = value[key];
    });
    return Object.keys(result).length ? result : null;
  }

  function normalizeCompliance(value) {
    if (!value || typeof value !== 'object' || value.ready !== true) return null;
    const dataRegion = normalizeIdentityValue(value.dataRegion, 16).toLowerCase();
    const dataLocation = normalizeIdentityValue(value.dataLocation, 200);
    const retentionPeriod = normalizeIdentityValue(value.retentionPeriod, 120);
    const privacyContact = normalizeIdentityValue(value.privacyContact, 256);
    const residency = MIXPANEL_RESIDENCIES[dataRegion];
    if (!residency || dataLocation !== residency.dataLocation || !retentionPeriod || !privacyContact) return null;
    return { apiHost: residency.apiHost, dataLocation, dataRegion, privacyContact, retentionPeriod };
  }

  function normalizeCoreUrl(value) {
    if (typeof value !== 'string' || !value.trim() || !window.location || !window.URL) return '';
    try {
      const url = new window.URL(value, window.location.href);
      const current = new window.URL(window.location.href);
      const isShopifyCdn = url.hostname === 'cdn.shopify.com' || /\.shopifycdn\.com$/.test(url.hostname);
      const isSameStore = url.origin === current.origin;
      if (
        url.protocol !== 'https:' ||
        (!isSameStore && !isShopifyCdn) ||
        !/\/fwd-mixpanel-core-2\.83\.0\.js$/.test(url.pathname)
      ) {
        return '';
      }
      return url.href;
    } catch (_error) {
      return '';
    }
  }

  function readExclusion() {
    try {
      return window.localStorage && window.localStorage.getItem(EXCLUSION_KEY) === '1';
    } catch (_error) {
      return false;
    }
  }

  function writeExclusion(excluded) {
    try {
      if (!window.localStorage) return;
      if (excluded) window.localStorage.setItem(EXCLUSION_KEY, '1');
      else window.localStorage.removeItem(EXCLUSION_KEY);
    } catch (_error) {
      // Storage denial must never affect storefront behavior.
    }
  }

  function bindPrivacyChanges() {
    if (!document.addEventListener) return;
    document.addEventListener('visitorConsentCollected', evaluatePrivacy);
  }

  function startPrivacyGate() {
    if (state.privacyBootstrapStartedAt === null) state.privacyBootstrapStartedAt = Date.now();
    loadPrivacyApi();
  }

  function clearPrivacyBootstrapTimer() {
    if (state.privacyBootstrapTimer !== null) window.clearTimeout(state.privacyBootstrapTimer);
    state.privacyBootstrapTimer = null;
  }

  function loadPrivacyApi() {
    clearPrivacyBootstrapTimer();
    if (state.excluded) {
      denyTracking('excluded');
      return;
    }
    if (hasPrivacyApi()) {
      state.privacyLoadGeneration += 1;
      evaluatePrivacy();
      return;
    }
    const remaining = PRIVACY_BOOTSTRAP_TIMEOUT_MS - (Date.now() - state.privacyBootstrapStartedAt);
    if (remaining <= 0 || state.privacyLoadAttempts >= MAX_PRIVACY_LOAD_ATTEMPTS) {
      state.privacyLoadGeneration += 1;
      denyTracking('unavailable');
      return;
    }
    state.privacy = 'loading';
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      state.privacyBootstrapTimer = window.setTimeout(loadPrivacyApi, Math.min(PRIVACY_BOOTSTRAP_RETRY_MS, remaining));
      return;
    }
    const generation = ++state.privacyLoadGeneration;
    state.privacyLoadAttempts += 1;
    state.privacyBootstrapTimer = window.setTimeout(function () {
      if (generation !== state.privacyLoadGeneration) return;
      state.privacyLoadGeneration += 1;
      clearPrivacyBootstrapTimer();
      denyTracking('unavailable');
    }, remaining);
    try {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.2' }],
        function (error) {
          if (generation !== state.privacyLoadGeneration) return;
          clearPrivacyBootstrapTimer();
          if (!error && hasPrivacyApi()) {
            state.privacyLoadGeneration += 1;
            evaluatePrivacy();
            return;
          }
          state.privacy = 'loading';
          state.privacyBootstrapTimer = window.setTimeout(loadPrivacyApi, PRIVACY_BOOTSTRAP_RETRY_MS);
        }
      );
    } catch (_error) {
      clearPrivacyBootstrapTimer();
      state.privacy = 'loading';
      state.privacyBootstrapTimer = window.setTimeout(loadPrivacyApi, PRIVACY_BOOTSTRAP_RETRY_MS);
    }
  }

  function hasPrivacyApi() {
    return Boolean(
      window.Shopify &&
      window.Shopify.customerPrivacy &&
      typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function'
    );
  }

  function evaluatePrivacy() {
    if (state.excluded || hasPrivacyApi()) {
      clearPrivacyBootstrapTimer();
      state.privacyLoadGeneration += 1;
    }
    if (state.excluded) {
      denyTracking('excluded');
      return false;
    }

    try {
      if (!hasPrivacyApi() || window.Shopify.customerPrivacy.analyticsProcessingAllowed() !== true) {
        denyTracking(hasPrivacyApi() ? 'denied' : 'unavailable');
        return false;
      }
    } catch (_error) {
      denyTracking('unavailable');
      return false;
    }

    state.privacy = 'allowed';
    configureEnvironment();
    if (!config.compliance || !validToken(config.token)) {
      ensureSdk();
      clearAnalyticsLifecycle();
      return false;
    }
    prepareIdentityBoundary();
    ensureGeography();
    // A publish function may exist before the consent-aware pixel starts. Do not
    // create initial facts or visible intervals until its current-page ack.
    if (pixelMode() && !ensureSdk()) return false;
    const lifecycle = prepareBehaviorTracking();
    if (!ensureSdk()) return false;
    emitInitialLifecycle(lifecycle);
    return true;
  }

  function denyTracking(reason) {
    if (reason === 'denied' || reason === 'excluded') {
      clearPrivacyBootstrapTimer();
      state.privacyLoadGeneration += 1;
    }
    state.privacy = reason;
    clearGeography();
    state.pixelReadyNonce = null;
    state.queue = [];
    cancelCoreLoad();
    clearAnalyticsLifecycle();
    if (state.instance) {
      safeCall(state.instance, 'opt_out_tracking');
      state.wasOptedOut = true;
    }
  }

  function clearAnalyticsLifecycle() {
    clearEngagementTimer();
    clearExposures();
    state.hiddenAt = null;
    state.lastVisibleAt = Date.now();
    state.lifecycleGeneration += 1;
    state.navigation = null;
    state.pageViewId = null;
    state.engagementStartedAt = null;
    state.navigationFailure = null;
    state.pageViewed = false;
    state.productViewed = false;
    state.listRenderId = null;
    state.filterStateId = null;
    state.tabId = null;
    state.scrollCheckpoints.clear();
    state.scrollScheduled = false;
    state.sessionId = null;
    state.sessionLastActivityAt = 0;
    state.sessionLoaded = true;
    try {
      if (!window.sessionStorage) return;
      window.sessionStorage.removeItem(NAVIGATION_KEY);
      window.sessionStorage.removeItem(SESSION_KEY);
      window.sessionStorage.removeItem('fwd:analytics:qa');
      window.sessionStorage.removeItem('fwd:analytics:tab');
    } catch (_error) {
      // A fresh memory-only analytics lifecycle is sufficient when storage is unavailable.
    }
  }

  function ensureGeography() {
    // The native pixel/QA collector has a separate contract. This lookup belongs
    // to the currently deployed legacy collector only.
    if (pixelMode() || state.geographyRequested || state.identityFailed
      || typeof window.fetch !== 'function' || typeof window.AbortController !== 'function') return;
    state.geographyRequested = true;
    const generation = state.geographyGeneration;
    const controller = new window.AbortController();
    state.geographyController = controller;
    const timer = window.setTimeout(function () { controller.abort(); }, LOCATION_TIMEOUT_MS);
    state.geographyTimer = timer;
    window.fetch(LOCATION_URL, {
      method: 'GET', credentials: 'omit', referrerPolicy: 'no-referrer',
      cache: 'no-store', signal: controller.signal,
    }).then(function (response) {
      if (!response.ok) return null;
      return response.json();
    }).then(function (location) {
      if (generation !== state.geographyGeneration || state.privacy !== 'allowed' || state.excluded
        || state.identityFailed || controller.signal.aborted || !location) return;
      const country = location.country;
      const region = location.region;
      if (typeof country !== 'string' || !/^[A-Z]{2}$/.test(country) || country === 'XX') return;
      state.geography = { mp_country_code: country };
      if (typeof region === 'string' && region.trim() && region.trim().length <= 100
        && !/[\x00-\x1f\x7f<>@]/.test(region) && !containsSensitivePattern(region)) {
        state.geography.$region = region.trim();
      }
      registerGeography();
    }).catch(function () {
      // Unknown location must never interrupt analytics or shopping.
    }).finally(function () {
      window.clearTimeout(timer);
      if (generation === state.geographyGeneration) {
        state.geographyController = null;
        state.geographyTimer = null;
      }
    });
  }

  function registerGeography() {
    if (!state.instance || !state.geography.mp_country_code || state.privacy !== 'allowed' || state.excluded || state.identityFailed) return;
    // Native autocapture shares this context without writing it to persistence.
    safeCall(state.instance, 'register', [state.geography, { persistent: false }]);
  }

  function clearGeography() {
    state.geographyGeneration += 1;
    state.geography = {};
    state.geographyRequested = false;
    if (state.geographyController) state.geographyController.abort();
    if (state.geographyTimer !== null) window.clearTimeout(state.geographyTimer);
    state.geographyController = null;
    state.geographyTimer = null;
    if (state.instance) {
      ['mp_country_code', '$region'].forEach(function (key) {
        safeCall(state.instance, 'unregister', [key, { persistent: false }]);
      });
    }
  }

  function ensureSdk() {
    if (state.excluded || state.privacy !== 'allowed' || state.identityFailed) return false;
    if (!config.compliance) {
      cancelCoreLoad();
      state.sdk = 'disabled';
      state.queue = [];
      return false;
    }
    if (!validToken(config.token)) {
      cancelCoreLoad();
      state.sdk = 'disabled';
      state.queue = [];
      return false;
    }
    if (pixelMode()) {
      if (!bridge) { state.sdk = 'failed'; return false; }
      if (state.sdk === 'ready') return true;
      if (state.sdk === 'loading' || state.sdk === 'failed') return false;
      state.sdk = 'loading';
      const generation = ++state.coreLoadGeneration;
      const startedAt = Date.now();
      const nonce = state.pixelReadyNonce = createId('ready');
      const pagePath = safePagePath();
      const environment = qaMode() ? 'qa' : 'production';
      let synchronous = true;
      const checkPublisher = function () {
        if (generation !== state.coreLoadGeneration || state.privacy !== 'allowed' || state.excluded) return;
        if (Date.now() - startedAt >= SDK_LOAD_TIMEOUT_MS) {
          state.sdk = 'failed'; state.queue = []; clearSdkTimer(); return;
        }
        const matchesAck = function () {
          try {
            const ack = JSON.parse(window.sessionStorage.getItem(PIXEL_READY_KEY) || 'null');
            return ack?.protocol === 1 && ack.nonce === nonce && ack.page_path === pagePath
              && ack.environment === environment && ack.revision === 'v3-20261024-r3'
              && Number.isFinite(ack.ready_at) && ack.ready_at >= startedAt && ack.ready_at <= Date.now()
              && Date.now() - ack.ready_at <= 5000;
          } catch (_) { return false; }
        };
        if (typeof window.Shopify?.analytics?.publish === 'function') {
          try {
            const result = window.Shopify.analytics.publish('fwd:readiness', {
              protocol: 1, nonce, page_path: pagePath, environment,
            });
            if (result && typeof result.catch === 'function') result.catch(function () {});
          } catch (_) { /* A failed request cannot establish readiness. */ }
          if (matchesAck()) {
            state.sdk = 'ready'; clearSdkTimer();
            if (!synchronous) evaluatePrivacy();
            return;
          }
        }
        if (Date.now() - startedAt >= SDK_LOAD_TIMEOUT_MS) {
          state.sdk = 'failed'; state.queue = []; clearSdkTimer();
        } else state.sdkTimer = window.setTimeout(checkPublisher, 200);
      };
      checkPublisher();
      synchronous = false;
      return state.sdk === 'ready';
    }
    if (state.sdk === 'ready') {
      if (state.wasOptedOut) {
        safeCall(state.instance, 'opt_in_tracking');
        state.wasOptedOut = false;
      }
      if (!syncIdentity()) return false;
      flushQueue();
      return true;
    }
    if (state.sdk === 'loading') return true;
    if (state.sdk === 'failed') return false;
    return loadLocalCore();
  }

  function loadLocalCore() {
    if (!config.coreUrl || !document.createElement) {
      failSdk();
      return false;
    }

    const target = document.head || document.documentElement;
    if (!target || typeof target.appendChild !== 'function') {
      failSdk();
      return false;
    }

    const generation = state.coreLoadGeneration + 1;
    const script = document.createElement('script');
    if (!script) {
      failSdk();
      return false;
    }

    state.coreLoadGeneration = generation;
    state.coreScript = script;
    state.sdk = 'loading';
    script.async = true;
    script.dataset.fwdMixpanelCore = 'true';
    script.src = config.coreUrl;
    script.onload = function () {
      if (!canInitializeCore(generation)) return;
      clearSdkTimer();
      state.coreScript = null;
      const localCore = window.FwdMixpanelCore;
      const mixpanel = localCore && (localCore.default || localCore);
      if (!mixpanel || typeof mixpanel.init !== 'function') {
        failSdk(generation);
        return;
      }
      initializeMixpanel(mixpanel, generation);
    };
    script.onerror = function () {
      failSdk(generation);
    };
    state.sdkTimer = window.setTimeout(function () {
      failSdk(generation);
    }, SDK_LOAD_TIMEOUT_MS);

    try {
      target.appendChild(script);
      return true;
    } catch (_error) {
      failSdk(generation);
      return false;
    }
  }

  function canInitializeCore(generation) {
    return Boolean(
      generation === state.coreLoadGeneration &&
      !state.excluded &&
      state.privacy === 'allowed' &&
      !state.identityFailed &&
      Boolean(config.compliance) &&
      validToken(config.token)
    );
  }

  function cancelCoreLoad() {
    state.coreLoadGeneration += 1;
    clearSdkTimer();
    const script = state.coreScript;
    state.coreScript = null;
    if (script) {
      script.onload = null;
      script.onerror = null;
      if (script.parentNode && typeof script.parentNode.removeChild === 'function') {
        try {
          script.parentNode.removeChild(script);
        } catch (_error) {
          // A browser may already have detached the pending script.
        }
      }
    }
    if (!state.instance) state.sdk = 'idle';
  }

  function clearSdkTimer() {
    if (state.sdkTimer !== null) window.clearTimeout(state.sdkTimer);
    state.sdkTimer = null;
  }

  function initializeMixpanel(mixpanel, generation) {
    if (!canInitializeCore(generation)) return;
    try {
      purgeMixpanelBatchQueues(config.token);
      const initialized = mixpanel.init(config.token, {
        api_host: config.compliance.apiHost,
        autocapture: {
          block_attrs: ['href'],
          block_selectors: AUTOCAPTURE_BLOCK_SELECTORS,
          capture_text_content: false,
          click: true,
          dead_click: true,
          input: true,
          pageview: 'url-with-path',
          rage_click: { interactive_elements_only: true },
          scroll: true,
          scroll_capture_all: false,
          scroll_depth_percent_checkpoints: SCROLL_CHECKPOINTS,
          submit: true,
        },
        batch_requests: false,
        ip: false,
        persistence: 'localStorage',
        persistence_name: 'fwd_analytics_core_v2_' + config.token,
        property_blacklist: AUTOMATIC_SENSITIVE_PROPERTIES,
        save_referrer: false,
        skip_first_touch_marketing: true,
        stop_utm_persistence: true,
        store_google: true,
        track_marketing: false,
        track_pageview: false,
      });
      state.instance = initialized && typeof initialized.track === 'function' ? initialized : mixpanel;
      if (!canInitializeCore(generation)) {
        state.instance = null;
        return;
      }
      state.sdk = 'ready';
      if (state.wasOptedOut) {
        safeCall(state.instance, 'opt_in_tracking');
        state.wasOptedOut = false;
      }
      if (!syncIdentity()) return;
      flushQueue();
      registerGeography();
    } catch (_error) {
      failSdk(generation);
    }
  }

  function purgeMixpanelBatchQueues(token) {
    try {
      MIXPANEL_BATCH_QUEUE_SUFFIXES.forEach(function (suffix) {
        window.localStorage.removeItem('__mpq_' + token + suffix);
      });
    } catch (_error) {
      // Batching remains disabled, so an inaccessible stale queue cannot flush.
    }
  }

  function failSdk(generation) {
    if (generation !== undefined && generation !== state.coreLoadGeneration) return;
    if (state.sdk === 'ready') return;
    state.coreLoadGeneration += 1;
    clearSdkTimer();
    if (state.coreScript) {
      state.coreScript.onload = null;
      state.coreScript.onerror = null;
    }
    state.coreScript = null;
    state.sdk = 'failed';
    state.queue = [];
  }

  function track(eventName, properties, options) {
    if (!Object.hasOwn(EVENT_PROPERTIES, eventName) || state.excluded || state.identityFailed) return false;
    if (state.privacy === 'allowed' && state.behaviorBound && SESSION_ACTIVITY_EVENTS.has(eventName)) {
      if (ensureSession()) trackPrepared('Session Started', {}, null);
    }
    return trackPrepared(eventName, properties, options);
  }

  function trackPrepared(eventName, properties, options) {
    if (pixelMode() && state.sdk !== 'ready') return false;
    const context = commonContext();
    if (eventName === 'Page Viewed' || eventName === 'Product Viewed' || eventName === 'Page Engagement'
      || (eventName === 'UI Interaction' && config.page.type === 'product')
      || (/^Wishlist Item /.test(eventName) && config.page.type === 'product'
        && normalizeShopifyId(String(properties?.product_id || ''), 'Product') === state.catalog.product?.product_id)) {
      Object.assign(context, state.catalog.product || {});
    }
    const contextualProperties = Object.assign({}, semanticEventContext(eventName, properties), compactObject(properties || {}), context);
    const event = {
      name: eventName,
      options: options || null,
      properties: normalizeProperties(eventName, contextualProperties),
      queuedAt: Date.now(),
      eventId: pixelMode() ? properties?.engagement_segment_id || createId('event') : null,
    };

    if (
      state.privacy !== 'allowed' ||
      state.sdk === 'disabled' ||
      state.sdk === 'failed' ||
      state.identityFailed
    ) {
      return false;
    }
    if (state.privacy === 'allowed' && state.sdk === 'ready') {
      return send(event);
    }

    purgeQueue();
    if (state.queue.length >= MAX_QUEUE_SIZE) state.queue.shift();
    state.queue.push(event);
    return true;
  }

  function normalizeProperties(eventName, properties) {
    const source = properties && typeof properties === 'object' ? properties : {};
    const allowed = new Set(COMMON_PROPERTIES.concat(EVENT_PROPERTIES[eventName] || []));
    const result = {};

    for (const key of allowed) {
      if (!Object.hasOwn(source, key)) continue;
      let value;
      if (key === 'active_filters' || key === 'navigation_source_active_filters') value = normalizeActiveFilters(source[key]);
      else if (key === 'mp_country_code' || key === '$region') value = state.geography[key];
      else if (key === 'price') value = normalizeCurrency(source.currency) ? normalizeMoney(source[key]) : undefined;
      else if (key === 'filter_value_id') value = normalizeFilterValue(source.filter_param, source[key]);
      else if (key === 'before_filter_tokens' || key === 'after_filter_tokens') value = Array.isArray(source[key]) ? source[key].slice(0, 20).filter(function (entry) { return typeof entry === 'string' && entry.length <= 280 && !containsSensitivePattern(entry); }) : undefined;
      else value = normalizeValue(key, source[key]);
      if (value !== undefined) result[key] = value;
    }
    if (result.product_category) result.product_category_source = 'shopify_product_type';
    if (Object.hasOwn(source, 'price')) {
      const money = measurement.resolveMoney({ raw_value: source.price, currency: source.currency,
        source: 'shopify_liquid_hundredths', unit: 'shopify_hundredths' });
      result.price_status = money.status;
      result.price_conversion = money.rule;
      result.price_source = 'shopify_liquid_hundredths';
      result.price_basis = 'displayed_variant';
      if (money.status === 'resolved') {
        result.price_raw_value = money.raw_value;
        result.price_raw_unit = 'shopify_hundredths';
        result.unit_price = money.unit_price;
        result.price_currency = money.currency;
        result.unit_price_unit = 'major';
      }
    }
    if (Object.hasOwn(source, 'active_filters')) Object.assign(result,
      measurement.projectFilters(source.active_filters, { truncated: source.active_filters_truncated }));
    if (source._filterInputStatus === 'unavailable') result.filter_state_status = 'unavailable';
    if (Object.hasOwn(source, 'navigation_source_active_filters')) {
      const projection = measurement.projectFilters(source.navigation_source_active_filters,
        { truncated: source.navigation_source_active_filters_truncated });
      Object.keys(projection).forEach(function (key) { result['navigation_source_' + key] = projection[key]; });
    }
    return result;
  }

  function normalizeValue(key, value) {
    if (value === null || value === undefined || value === '') return undefined;
    if (URL_PROPERTIES.has(key)) {
      return normalizeSafeUrl(value, !['previous_page_url', 'referrer'].includes(key));
    }
    if (key === 'product_category') return safePublicLabel(value);
    if (LIST_PROPERTIES.has(key)) return normalizeStringList(value);
    if (BOOLEAN_PROPERTIES.has(key)) return value === true;
    if (NUMERIC_PROPERTIES.has(key)) {
      const number = Number(value);
      return Number.isFinite(number) ? Math.round(number) : undefined;
    }
    if (typeof value === 'boolean') return value;
    if (typeof value !== 'string' && typeof value !== 'number') return undefined;

    let normalized = String(value).trim();
    if (!normalized) return undefined;
    if (key === 'product_id' || key === 'navigation_source_product_id') normalized = normalizeShopifyId(normalized, 'Product');
    if (key === 'variant_id' || key === 'navigation_source_variant_id') normalized = normalizeShopifyId(normalized, 'ProductVariant');
    if (key === 'brand_id') normalized = normalizeMetaobjectId(normalized);
    if (key === 'search_query' || key === 'navigation_element_label') normalized = redactSearchQuery(normalized);
    if (key === 'collection_handle' || key === 'brand_handle' || key === 'navigation_source_collection_handle') normalized = normalizeHandle(normalized);
    if (key === 'currency') normalized = normalizeCurrency(normalized);
    if (normalized === undefined) return undefined;
    const limit = key === 'search_query' ? 100 : 256;
    return normalized.slice(0, limit);
  }

  function normalizeSafeUrl(value, sameOrigin = true) {
    if (typeof value !== 'string' || !value.trim() || !window.URL || !window.location) return undefined;
    try {
      const url = new window.URL(value, window.location.origin);
      if (url.protocol !== 'https:' || (sameOrigin && url.origin !== window.location.origin) || url.username || url.password) return undefined;
      const normalized = url.origin + url.pathname;
      return normalized.length <= 2048 ? normalized : undefined;
    } catch (_error) {
      return undefined;
    }
  }

  function normalizeShopifyId(value, type) {
    if (value.indexOf('gid://shopify/') === 0) {
      return new RegExp('^gid://shopify/' + type + '/\\d+$').test(value) ? value : undefined;
    }
    return /^\d+$/.test(value) ? 'gid://shopify/' + type + '/' + value : undefined;
  }

  function redactSearchQuery(value) {
    return value
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
      .replace(/(?:\+?82[- .]?)?0?1[016789][- .]?\d{3,4}[- .]?\d{4}/g, '[phone]')
      .replace(/\b(?:order|주문)[- _:#]*[A-Z0-9]{5,}\b/gi, '[order]')
      .replace(/\b[A-Z0-9]{9}\b/gi, '[order]');
  }

  function purgeQueue() {
    const cutoff = Date.now() - MAX_QUEUE_AGE_MS;
    state.queue = state.queue.filter(function (event) {
      return event.queuedAt >= cutoff;
    });
  }

  function flushQueue() {
    if (state.sdk !== 'ready' || state.privacy !== 'allowed' || state.excluded || state.identityFailed) return;
    purgeQueue();
    const pending = state.queue.splice(0, state.queue.length);
    pending.forEach(send);
  }

  function syncIdentity() {
    if (state.excluded || state.privacy !== 'allowed' || state.sdk !== 'ready' || !state.instance || state.identityFailed) {
      return false;
    }

    const previousId = loadIdentifiedCustomerId();
    const customer = config.customer;
    const customerId = customer && customer.id;

    if (previousId && previousId !== customerId) {
      if (!safeCall(state.instance, 'reset')) return failIdentity();
      saveIdentifiedCustomerId(null);
      registerGeography();
    }

    if (!customerId) return true;

    if (!safeCall(state.instance, 'identify', ['shopify-customer-' + customerId])) return failIdentity();
    saveIdentifiedCustomerId(customerId);
    if (!state.instance.people || !safeCall(state.instance.people, 'set', [customerProfile(customer)])) {
      return failIdentity();
    }
    return true;
  }

  function failIdentity() {
    state.identityFailed = true;
    state.queue = [];
    return false;
  }

  function prepareIdentityBoundary() {
    if (pixelMode()) return false;
    const previousId = loadIdentifiedCustomerId();
    const customerId = config.customer && config.customer.id;
    if (!previousId || previousId === customerId) return false;
    state.navigation = null;
    state.sessionId = null;
    state.sessionLastActivityAt = 0;
    state.sessionLoaded = true;
    try {
      if (!window.sessionStorage) return true;
      window.sessionStorage.removeItem(NAVIGATION_KEY);
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch (_error) {
      // A memory-only identity boundary is sufficient when storage is unavailable.
    }
    return true;
  }

  function customerProfile(customer) {
    const profile = {
      logged_in: true,
      shopify_customer_id: customer.id,
    };
    if (customer.email) profile.$email = customer.email;
    if (customer.name) profile.$name = customer.name;
    if (isValidAccountCreatedAt(customer.accountCreatedAt)) {
      profile.account_created_at = customer.accountCreatedAt;
    }
    return profile;
  }

  function normalizeCustomer(value) {
    if (!value || typeof value !== 'object' || value.loggedIn !== true) return null;
    const id = normalizeIdentityValue(value.id, 64);
    if (!id) return null;
    return {
      accountCreatedAt: normalizeIdentityValue(value.accountCreatedAt, 64),
      email: normalizeIdentityValue(value.email, 256),
      id,
      name: normalizeIdentityValue(value.name, 256),
    };
  }

  function normalizeIdentityValue(value, limit) {
    if (typeof value !== 'string' && typeof value !== 'number') return undefined;
    const normalized = String(value).trim();
    return normalized ? normalized.slice(0, limit) : undefined;
  }

  function isValidAccountCreatedAt(value) {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(value);
  }

  function loadIdentifiedCustomerId() {
    if (state.identityLoaded) return state.identifiedCustomerId;
    state.identityLoaded = true;
    try {
      if (!window.localStorage) return null;
      const value = window.localStorage.getItem(IDENTITY_KEY);
      state.identifiedCustomerId = normalizeIdentityValue(value, 64) || null;
    } catch (_error) {
      state.identifiedCustomerId = null;
    }
    return state.identifiedCustomerId;
  }

  function saveIdentifiedCustomerId(customerId) {
    state.identityLoaded = true;
    state.identifiedCustomerId = customerId || null;
    try {
      if (!window.localStorage) return;
      if (customerId) window.localStorage.setItem(IDENTITY_KEY, customerId);
      else window.localStorage.removeItem(IDENTITY_KEY);
    } catch (_error) {
      // Identity storage denial must never affect storefront behavior.
    }
  }

  function send(event) {
    if (pixelMode()) {
      if (state.privacy !== 'allowed' || state.excluded || !bridge) return false;
      const payload = bridge.project(event.name, event.properties, {
        event_id: event.eventId, occurred_at: new Date(event.queuedAt).toISOString(),
        collector_revision: config.collectorRevision, environment: qaMode() ? 'qa' : 'production',
        sequence: ++state.sequence,
      });
      if (!payload) return false;
      try {
        const result = window.Shopify.analytics.publish('fwd:measurement', payload);
        if (result && typeof result.catch === 'function') result.catch(function () {});
        return true;
      } catch (_) { return false; }
    }
    if (!state.instance || typeof state.instance.track !== 'function') return false;
    try {
      state.instance.track(event.name, event.properties, event.options || undefined);
      return true;
    } catch (_error) {
      return false;
    }
  }

  function excludeCurrentBrowser() {
    state.excluded = true;
    writeExclusion(true);
    denyTracking('excluded');
    return true;
  }

  function includeCurrentBrowser() {
    writeExclusion(false);
    state.excluded = Boolean(config.excluded);
    state.privacy = 'unknown';
    if (!state.excluded && !hasPrivacyApi()) {
      state.privacyBootstrapStartedAt = null;
      state.privacyLoadAttempts = 0;
      state.privacyLoadGeneration += 1;
      startPrivacyGate();
    } else if (!state.excluded) evaluatePrivacy();
    return !state.excluded;
  }

  function status() {
    purgeQueue();
    return {
      excluded: state.excluded,
      complianceReady: Boolean(config.compliance),
      privacy: state.privacy,
      queued: state.queue.length,
      replay: 'deferred',
      sdk: state.sdk,
      sessionId: state.sessionId,
      tokenConfigured: validToken(config.token),
      transport: pixelMode() ? 'shopify_custom_pixel' : 'legacy_theme_sdk',
      measurementVersion: pixelMode() ? 3 : 2,
      environment: qaMode() ? 'qa' : 'production',
    };
  }

  function setCatalogContext(value, commit) {
    const before = measurement.projectFilters(state.catalog?.active_filters,
      { truncated: state.catalog?.active_filters_truncated });
    const previousId = state.filterStateId;
    const next = normalizeCatalogContext(value);
    const after = measurement.projectFilters(next.active_filters, { truncated: next.active_filters_truncated });
    const changed = before.filter_tokens_key !== after.filter_tokens_key || before.filter_state_status !== after.filter_state_status;
    state.catalog = next;
    if (behaviorTrackingAllowed()) {
      if (pixelMode()) {
        state.listRenderId = createId('list');
        if (changed || !state.filterStateId) state.filterStateId = createId('filters');
      }
      observeProducts();
      if (commit?.applied === true && commit.filterChanged === true && changed) {
        track('Product Filters Applied', { request_id: String(commit.requestId), action_id: createId('action'),
          previous_filter_state_id: previousId, before_filter_tokens: before.filter_tokens,
          after_filter_tokens: after.filter_tokens, change_type: commit.changeType || 'batch',
          applied: true, result_count: commit.resultCount });
      }
    }
    return true;
  }

  function setProductContext(value) {
    if (config.page.type !== 'product') return false;
    const product = normalizeProductContext(value);
    if (!product?.product_id || product.product_id !== state.catalog.product?.product_id) return false;
    state.catalog.product = product;
    if (behaviorTrackingAllowed()) track('UI Interaction', {
      component_key: 'product_variant', component_label: '상품 옵션', component_role: 'select',
      action: 'select_variant', result: 'applied', placement: 'product_detail', interaction_id: createId('interaction'),
    });
    return true;
  }

  function prepareBehaviorTracking() {
    if (!state.behaviorBound) bindBehaviorTracking();
    const isFreshLifecycle = !state.pageViewed;
    if (isFreshLifecycle) {
      state.hiddenAt = null;
      state.lastVisibleAt = Date.now();
      state.pageViewId = createId('page');
      if (pixelMode()) {
        state.listRenderId = createId('list');
        state.filterStateId = createId('filters');
      }
      if (document.visibilityState !== 'hidden') beginEngagement();
      observeProducts();
    }
    const sessionStarted = ensureSession();
    let pageViewedProperties = null;
    if (!state.pageViewed) {
      state.navigation = consumeNavigationEnvelope() || referrerNavigation();
      pageViewedProperties = state.navigation;
      state.pageViewed = true;
    }
    return { pageViewedProperties, sessionStarted };
  }

  function emitInitialLifecycle(lifecycle) {
    if (!lifecycle) return;
    if (lifecycle.sessionStarted) trackPrepared('Session Started', {}, null);
    if (lifecycle.pageViewedProperties) trackPrepared('Page Viewed', Object.assign({}, lifecycle.pageViewedProperties, capabilityFootprint()), null);
    if (lifecycle.pageViewedProperties && state.catalog.product && !state.productViewed) {
      state.productViewed = true;
      trackPrepared('Product Viewed', {}, null);
    }
  }

  function bindBehaviorTracking() {
    state.behaviorBound = true;
    state.lastVisibleAt = Date.now();
    if (document.addEventListener) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      document.addEventListener('click', handleDocumentClick, true);
    }
    if (window.addEventListener) {
      window.addEventListener('pagehide', handlePageHide);
      window.addEventListener('pageshow', function (event) {
        if (event.persisted && behaviorTrackingAllowed() && document.visibilityState !== 'hidden' && state.engagementStartedAt === null) {
          state.hiddenAt = null;
          state.lastVisibleAt = Date.now();
          beginEngagement();
          observeProducts(false);
        }
      });
      window.addEventListener('scroll', scheduleScrollCheck, { passive: true });
    }
  }

  function capabilityFootprint() {
    const has = function (selector) {
      try { return Boolean(document.querySelector?.(selector)); } catch (_) { return false; }
    };
    const cards = has('[data-fwd-event="Product Clicked"][data-fwd-product-id]');
    return {
      supports_product_click: cards,
      supports_wishlist: has('.fwd-wishlist-button[data-product-id]'),
      supports_filters: has('facet-filters-form'),
      supports_components: has('header button, [data-fwd-event="UI Interaction"], .product__accordion summary, .product__media-toggle'),
      supports_journeys: Boolean(window.sessionStorage) && cards,
      supports_exposures: cards && Boolean(state.exposureObserver),
    };
  }

  function closeEngagementSegment(cause) {
    clearEngagementTimer();
    if (state.engagementStartedAt === null) return;
    const elapsed = Math.max(0, monotonicNow() - state.engagementStartedAt);
    const startedAt = state.engagementStartedWall;
    state.engagementStartedAt = null;
    if (!elapsed) return;
    const endedAt = Date.now();
    const discontinuity = Math.abs(endedAt - startedAt - elapsed) > 2;
    const emit = function (start, end, duration) {
      const segmentId = createId('engagement');
      track('Page Engagement', {
        visible_duration_ms: Math.min(duration, MAX_ENGAGEMENT_SEGMENT_MS),
        visible_duration_capped: elapsed > MAX_ENGAGEMENT_SEGMENT_MS,
        engagement_segment_id: segmentId, $insert_id: segmentId, lifecycle_cause: cause,
        segment_started_at: new Date(start).toISOString(), segment_ended_at: new Date(end).toISOString(),
        measurement_clock: window.performance?.now ? 'monotonic' : 'wall_clock_fallback',
        segment_clock_discontinuity: discontinuity,
      }, { send_immediately: true });
    };
    if (!pixelMode() || discontinuity || elapsed > MAX_ENGAGEMENT_SEGMENT_MS) {
      emit(startedAt, endedAt, elapsed);
      return;
    }
    // Half-open KST-day chunks keep a midnight boundary from moving the entire
    // previous day's visible interval into the next reporting period.
    const dayMs = 86400000;
    const boundary = (Math.floor((startedAt + 32400000) / dayMs) + 1) * dayMs - 32400000;
    if (boundary < endedAt) {
      const first = Math.min(elapsed, boundary - startedAt);
      emit(startedAt, boundary, first);
      emit(boundary, endedAt, elapsed - first);
    } else emit(startedAt, endedAt, elapsed);
  }

  function qaMode() {
    try { return state.qa || new window.URL(window.location.href).searchParams.get('fwd_measurement_qa') === '1'; }
    catch (_) { return false; }
  }

  function configureEnvironment() {
    if (state.privacy !== 'allowed') return;
    try {
      const requested = new window.URL(window.location.href).searchParams.get('fwd_measurement_qa');
      if (requested === '0') { state.qa = false; window.sessionStorage?.removeItem('fwd:analytics:qa'); }
      else {
        state.qa = requested === '1' || window.sessionStorage?.getItem('fwd:analytics:qa') === '1';
        if (state.qa) window.sessionStorage?.setItem('fwd:analytics:qa', '1');
      }
      if (!state.tabId && pixelMode()) {
        const priorTab = window.sessionStorage?.getItem('fwd:analytics:tab');
        state.tabId = typeof priorTab === 'string' && /^tab-[a-zA-Z0-9-]{1,80}$/.test(priorTab) ? priorTab : createId('tab');
        window.sessionStorage?.setItem('fwd:analytics:tab', state.tabId);
      }
    } catch (_) { /* QA stays memory-only when session storage is unavailable. */ }
  }

  function pixelMode() { return config.measurementMode === 'pixel' || qaMode(); }

  function monotonicNow() {
    return typeof window.performance?.now === 'function' ? window.performance.now() : Date.now();
  }

  function clearEngagementTimer() {
    if (state.engagementTimer !== null) window.clearTimeout(state.engagementTimer);
    state.engagementTimer = null;
  }

  function beginEngagement() {
    state.engagementStartedAt = monotonicNow();
    state.engagementStartedWall = Date.now();
    clearEngagementTimer();
    if (!pixelMode()) return;
    const generation = state.lifecycleGeneration;
    state.engagementTimer = window.setTimeout(function () {
      if (generation !== state.lifecycleGeneration || !behaviorTrackingAllowed() || document.visibilityState === 'hidden') return;
      closeEngagementSegment('periodic');
      beginEngagement();
    }, 15000);
  }

  function clearExposures(resetSeen = true) {
    if (state.exposureObserver) state.exposureObserver.disconnect();
    state.exposureObserver = null;
    state.exposureTimers.forEach(function (value) { window.clearTimeout(value.timer); });
    state.exposureTimers.clear();
    if (resetSeen) state.exposures.clear();
  }

  function observeProducts(resetSeen = true) {
    clearExposures(resetSeen);
    if (!pixelMode() || !behaviorTrackingAllowed() || !window.IntersectionObserver || !document.querySelectorAll) return;
    const listId = state.listRenderId;
    const generation = state.lifecycleGeneration;
    state.exposureObserver = new window.IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const element = entry.target;
        const key = element.dataset?.fwdProductId;
        if (!key) return;
        const prior = state.exposureTimers.get(element);
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || document.visibilityState === 'hidden') {
          if (prior) window.clearTimeout(prior.timer);
          state.exposureTimers.delete(element);
          return;
        }
        if (prior || state.exposures.has(key)) return;
        const timer = window.setTimeout(function () {
          state.exposureTimers.delete(element);
          if (listId !== state.listRenderId || generation !== state.lifecycleGeneration || !behaviorTrackingAllowed()
            || document.visibilityState === 'hidden' || element.isConnected === false || state.exposures.has(key)) return;
          state.exposures.add(key);
          track('Product Exposed', productProperties(element));
        }, 1000);
        state.exposureTimers.set(element, { timer });
      });
    }, { threshold: [0, 0.5, 1] });
    document.querySelectorAll('[data-fwd-event="Product Clicked"][data-fwd-product-id]').forEach(function (element) {
      state.exposureObserver.observe(element);
    });
  }

  function handleVisibilityChange() {
    if (!behaviorTrackingAllowed()) return;
    const now = Date.now();
    if (document.visibilityState === 'hidden') {
      if (state.hiddenAt !== null) return;
      closeEngagementSegment('hidden');
      clearExposures(false);
      track('Page Hidden', {});
      state.hiddenAt = now;
      return;
    }
    if (document.visibilityState === 'visible') {
      if (state.engagementStartedAt !== null) return;
      track('Page Visible', {
        hidden_duration_ms: state.hiddenAt === null ? 0 : Math.max(0, now - state.hiddenAt),
      });
      state.hiddenAt = null;
      state.lastVisibleAt = now;
      beginEngagement();
      observeProducts(false);
    }
  }

  function handlePageHide() {
    if (!behaviorTrackingAllowed()) return;
    closeEngagementSegment('pagehide');
    clearExposures(false);
    track('Session Exit Attempted', {}, { send_immediately: true });
  }

  function scheduleScrollCheck() {
    if (!behaviorTrackingAllowed()) return;
    if (state.scrollScheduled) return;
    state.scrollScheduled = true;
    const generation = state.lifecycleGeneration;
    const schedule = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame
      : function (callback) { callback(); };
    schedule(function () {
      if (generation !== state.lifecycleGeneration || !behaviorTrackingAllowed()) return;
      state.scrollScheduled = false;
      trackScrollCheckpoints(generation);
    });
  }

  function trackScrollCheckpoints(generation) {
    if (generation !== undefined && generation !== state.lifecycleGeneration) return;
    if (!behaviorTrackingAllowed()) return;
    const root = document.documentElement || {};
    const body = document.body || {};
    const pageHeight = Math.max(Number(root.scrollHeight) || 0, Number(body.scrollHeight) || 0);
    const viewportHeight = Number(window.innerHeight) || Number(root.clientHeight) || 0;
    if (pageHeight <= 0 || viewportHeight <= 0) return;
    const progress = Math.min(100, Math.max(0, ((Number(window.scrollY) || 0) + viewportHeight) / pageHeight * 100));
    SCROLL_CHECKPOINTS.forEach(function (checkpoint) {
      if (progress < checkpoint || state.scrollCheckpoints.has(checkpoint)) return;
      state.scrollCheckpoints.add(checkpoint);
      track('Page Scrolled', { scroll_depth: checkpoint });
    });
  }

  function handleDocumentClick(event) {
    if (!behaviorTrackingAllowed()) return;
    if (!event || event.defaultPrevented || (event.button !== undefined && event.button !== 0)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (state.handledClicks.has(event)) return;
    state.handledClicks.add(event);
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const semanticElement = target.closest('[data-fwd-event]');
    if (target.closest(AUTOCAPTURE_BLOCK_SELECTORS.join(','))) return;
    const control = target.closest('[data-fwd-analytics-control]');
    const controls = { menu_toggle: 'Menu toggle', search_toggle: 'Search toggle', cart_toggle: 'Cart toggle' };
    if (control && controls[control.dataset?.fwdAnalyticsControl]) {
      const key = control.dataset.fwdAnalyticsControl;
      track('UI Interaction', { component_key: key, component_name: key, component_label: controls[key],
        component_role: 'button', component_type: 'control', action: 'toggle', placement: 'header',
        interaction_id: createId('interaction') });
    }
    if (config.page.type === 'product') {
      const information = target.closest('.product__accordion summary');
      const media = target.closest('.product__media-toggle');
      if (information || media) track('UI Interaction', {
        component_key: information ? 'product_information' : 'product_media',
        component_label: information ? '상품 정보' : '상품 이미지', component_role: 'button',
        action: information ? 'toggle_information' : 'open_media', placement: 'product_detail',
        interaction_id: createId('interaction'),
      });
    }
    const navigationElement = target.closest('a[href]');
    if (navigationElement && !isSafePublicLink(navigationElement)) return;
    let navigationEvent = 'Link Clicked';
    let interactionId = createId('interaction');

    if (semanticElement && semanticElement.dataset && semanticElement.dataset.fwdEvent === 'Product Clicked') {
      navigationEvent = 'Product Clicked';
      track('Product Clicked', productProperties(semanticElement, interactionId));
      if (config.page.type === 'search') {
        const query = searchQueryFromLocation();
        if (query.length >= 2) {
          track('Search Result Selected', Object.assign(productProperties(semanticElement, interactionId), {
            surface: 'search_page',
            search_query: query,
            query_length: query.length,
          }));
        }
      }
    }

    if (navigationElement && navigationEvent !== 'Product Clicked') {
      track('Link Clicked', Object.assign(semanticLinkContext(navigationElement), {
        interaction_id: interactionId, destination_url: navigationElement.href, action: 'navigate',
      }));
    }
    if (navigationElement && isSameTabInternalLink(navigationElement)) {
      storeNavigationEnvelope(navigationElement, semanticElement, navigationEvent, interactionId);
    }
  }

  function productProperties(element, interactionId) {
    const data = element && element.dataset ? element.dataset : {};
    return {
      action: data.fwdAction,
      brand: data.fwdBrand,
      brand_id: data.fwdBrandId,
      component_name: data.fwdComponentName,
      component_key: safeSemanticKey(data.fwdAnalyticsKey) || 'product_card',
      component_label: 'Product card',
      component_role: 'link',
      component_type: data.fwdComponentType,
      currency: data.fwdCurrency,
      interaction_id: interactionId,
      list_name: data.fwdListName,
      list_position: data.fwdListPosition,
      placement: data.fwdPlacement,
      price: data.fwdPrice,
      product_id: data.fwdProductId,
      product_name: data.fwdProductName,
      product_category: data.fwdProductCategory,
      product_url: data.fwdProductUrl,
      brand_url: data.fwdBrandUrl,
      size: data.fwdSize,
      variant_title: data.fwdVariantTitle,
      variant_id: data.fwdVariantId,
    };
  }

  function searchQueryFromLocation() {
    try {
      const params = new window.URL(window.location.href).searchParams;
      const displayQuery = params.get('fwd_q');
      if (displayQuery) return displayQuery.trim().slice(0, 100);
      if (params.has('fwd_search_scope')) return '';
      return params.get('q')?.trim().slice(0, 100) || '';
    } catch (_error) {
      return '';
    }
  }

  function isSameTabInternalLink(element) {
    try {
      if (!element || (element.target && element.target !== '_self') || element.hasAttribute('download')) return false;
      const destination = new window.URL(element.href, window.location.href);
      const current = new window.URL(window.location.href);
      return destination.origin === current.origin &&
        (destination.pathname !== current.pathname || destination.search !== current.search);
    } catch (_error) {
      return false;
    }
  }

  function storeNavigationEnvelope(link, semanticElement, navigationEvent, interactionId) {
    if (!behaviorTrackingAllowed()) return;
    try {
      if (!window.sessionStorage) return;
      const destination = new window.URL(link.href, window.location.href);
      const semantic = semanticElement || link;
      const data = semantic.dataset || {};
      const destinationFilterIdentity = navigationFilterIdentity(destination);
      const envelope = {
        envelopeSchemaVersion: 2,
        sourceSessionId: state.sessionId,
        createdAt: Date.now(),
        destinationPath: destination.pathname,
        destinationFilterIdentity: destinationFilterIdentity.entries,
        destinationFilterIdentityRequired: true,
        destinationFilterIdentityTruncated: destinationFilterIdentity.truncated,
        navigation_interaction_id: interactionId,
        source_page_view_id: state.pageViewId,
        source_list_render_id: state.listRenderId,
        source_filter_state_id: state.filterStateId,
        previous_page_path: safePagePath(),
        previous_page_type: config.page.type,
        navigation_action: 'navigate',
        navigation_status: 'matched',
        navigation_source_list_name: data.fwdListName,
        navigation_source_list_position: data.fwdListPosition,
        navigation_source_collection_handle: state.catalog && state.catalog.collection_handle,
        navigation_source_active_filters: state.catalog && state.catalog.active_filters,
        navigation_source_active_filter_count: state.catalog && state.catalog.active_filter_count,
        navigation_source_active_filters_truncated: state.catalog && state.catalog.active_filters_truncated,
        navigation_element_key: navigationElementKey(link, data, destination),
        navigation_element_label: navigationElementLabel(link, data),
        navigation_element_role: 'link',
        navigation_event: navigationEvent,
        navigation_source_placement: data.fwdPlacement,
        previous_page_url: safeCurrentUrl(),
        navigation_source_product_id: data.fwdProductId,
        navigation_source_product_url: data.fwdProductUrl,
        navigation_source_variant_id: data.fwdVariantId,
      };
      window.sessionStorage.setItem(NAVIGATION_KEY, JSON.stringify(envelope));
    } catch (_error) {
      // Navigation must never depend on analytics storage.
    }
  }

  function consumeNavigationEnvelope() {
    try {
      if (!window.sessionStorage) return null;
      const raw = window.sessionStorage.getItem(NAVIGATION_KEY);
      window.sessionStorage.removeItem(NAVIGATION_KEY);
      if (!raw) return null;
      const envelope = JSON.parse(raw);
      const age = Date.now() - Number(envelope && envelope.createdAt);
      if (!envelope || typeof envelope.createdAt !== 'number' || !Number.isFinite(age) || age < 0 || age > NAVIGATION_TTL_MS) {
        state.navigationFailure = age > NAVIGATION_TTL_MS ? 'expired' : 'invalid';
        return null;
      }
      state.navigationFailure = 'unmatched';
      if (envelope.envelopeSchemaVersion !== 2 || envelope.sourceSessionId !== state.sessionId) return null;
      if (!envelope.destinationPath || !envelope.source_page_view_id || !normalizeSafeUrl(envelope.previous_page_url)) return null;
      const referrer = safeReferrer();
      if (referrer && referrer !== normalizeSafeUrl(envelope.previous_page_url)) return null;
      if (envelope.destinationPath && envelope.destinationPath !== safePagePath()) return null;
      if (envelope.destinationFilterIdentityRequired === true
        && !navigationFilterIdentityMatches(
          envelope.destinationFilterIdentity,
          envelope.destinationFilterIdentityTruncated === true
        )) return null;
      state.navigationFailure = null;
      delete envelope.envelopeSchemaVersion;
      delete envelope.sourceSessionId;
      delete envelope.createdAt;
      delete envelope.destinationPath;
      delete envelope.destinationFilterIdentity;
      delete envelope.destinationFilterIdentityRequired;
      delete envelope.destinationFilterIdentityTruncated;
      return normalizeProperties('Page Viewed', envelope);
    } catch (_error) {
      return null;
    }
  }

  function navigationFilterIdentity(url) {
    const identity = [];
    try {
      FILTER_PARAMS.forEach(function (param) {
        url.searchParams.getAll(param).forEach(function (rawValue) {
          String(rawValue).split(',').forEach(function (candidate) {
            const value = normalizeFilterValue(param, candidate);
            if (value !== undefined) identity.push(param + '\u0000' + value);
          });
        });
      });
    } catch (_error) {
      return { entries: [], truncated: true };
    }
    const entries = Array.from(new Set(identity)).sort();
    return { entries: entries.slice(0, MAX_ACTIVE_FILTERS), truncated: entries.length > MAX_ACTIVE_FILTERS };
  }

  function navigationFilterIdentityMatches(expected, expectedTruncated) {
    if (!Array.isArray(expected)) return false;
    if (expectedTruncated || state.catalog?.active_filters_truncated || expected.length > MAX_ACTIVE_FILTERS) return false;
    const current = Array.from(new Set((state.catalog?.active_filters || []).map(function (filter) {
      return filter.param + '\u0000' + filter.value;
    }))).sort();
    if (expected.length !== current.length) return false;
    return expected.every(function (entry, index) {
      return typeof entry === 'string' && entry.length <= 300 && entry === current[index];
    });
  }

  function referrerNavigation() {
    const referrer = safeReferrer();
    if (!referrer) return { navigation_event: 'direct', navigation_status: state.navigationFailure || 'direct' };
    try {
      const referrerUrl = new window.URL(referrer);
      return {
        navigation_event: referrerUrl.origin === window.location.origin ? 'internal' : 'external',
        previous_page_url: referrer,
        previous_page_path: referrerUrl.pathname,
        previous_page_type: publicPageType(referrerUrl.pathname),
        navigation_status: state.navigationFailure || 'referrer',
      };
    } catch (_error) {
      return { navigation_event: 'external' };
    }
  }

  function safeSemanticKey(value) {
    return typeof value === 'string' && /^[a-z][a-z0-9_-]{0,79}$/.test(value) ? value : undefined;
  }

  function safePublicLabel(value) {
    const label = normalizeIdentityValue(value, 120);
    return label && !containsSensitivePattern(label) ? label : undefined;
  }

  function normalizeMoney(value) {
    if (value === null || value === undefined || value === '') return undefined;
    const raw = Number(value);
    return Number.isSafeInteger(raw) && raw >= 0 ? raw / 100 : undefined;
  }

  function publicPageType(path) {
    const route = String(path || '').replace(/^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/)/, '');
    if (route === '/') return 'index';
    if (/^\/products\//.test(route)) return 'product';
    if (/^\/collections(?:\/|$)/.test(route)) return 'collection';
    if (/^\/search(?:\/|$)/.test(route)) return 'search';
    if (/^\/cart(?:\/|$)/.test(route)) return 'cart';
    return 'page';
  }

  function isSafePublicLink(link) {
    try {
      const url = new window.URL(link.href, window.location.href);
      return url.origin === window.location.origin && !/\/(?:account|checkout|checkouts|orders)(?:\/|$)/i.test(url.pathname);
    } catch (_error) { return false; }
  }

  function semanticLinkContext(link) {
    const data = link.dataset || {};
    const type = publicPageType(new window.URL(link.href, window.location.href).pathname);
    const path = new window.URL(link.href, window.location.href).pathname;
    const name = data.fwdEvent === 'Product Clicked' ? 'product_card'
      : /\/pages\/wishlist\/?$/.test(path) ? 'wishlist_link'
      : /\/pages\/brands(?:\/|$)/.test(path) ? 'brand_link' : type + '_link';
    let placement = 'page';
    if (typeof link.closest === 'function') {
      if (link.closest('header')) placement = 'header';
      else if (link.closest('footer')) placement = 'footer';
      else if (link.closest('nav')) placement = 'navigation';
    }
    return {
      component_key: safeSemanticKey(data.fwdAnalyticsKey) || name,
      component_name: safeSemanticKey(data.fwdComponentName) || name,
      component_type: 'link', component_role: 'link',
      component_label: { product_card: 'Product card', wishlist_link: 'Wishlist', brand_link: 'Brand directory' }[name] || type + ' link',
      placement: safeSemanticKey(data.fwdPlacement) || placement,
    };
  }

  function semanticEventContext(eventName, properties) {
    const placement = safeSemanticKey(properties && properties.placement) || config.page.type;
    let key = 'page_lifecycle';
    let role = 'page';
    let label = 'Page lifecycle';
    let action;
    if (eventName === 'Product Clicked') { key = 'product_card'; role = 'link'; label = 'Product card'; action = 'open_product'; }
    else if (/^Wishlist Item /.test(eventName)) { key = 'wishlist_button'; role = 'button'; label = 'Wishlist button'; action = /Added$/.test(eventName) ? 'save_product' : 'remove_saved_product'; }
    else if (eventName === 'Wishlist Cleared') { key = 'wishlist_clear'; role = 'button'; label = 'Clear wishlist'; action = 'clear'; }
    else if (eventName === 'Wishlist Added to Cart') { key = 'wishlist_cart_action'; role = 'button'; label = 'Wishlist add to cart'; action = 'add_to_cart'; }
    else if (eventName === 'Cart Item Added') { key = placement === 'wishlist' ? 'wishlist_cart_action' : 'product_form'; role = 'form'; label = 'Add to cart'; action = 'add_to_cart'; }
    else if (eventName === 'Search Result Selected') { key = 'search_result'; role = 'link'; label = 'Search product result'; action = 'open_product'; }
    else if (/^Search /.test(eventName)) { key = 'search'; role = /Submitted/.test(eventName) ? 'form' : /Query/.test(eventName) ? 'searchbox' : 'dialog'; label = 'Product search'; }
    else if (eventName === 'Product Filter Changed') { key = 'product_filter'; role = 'filter'; label = 'Product filter'; action = 'change_filter'; }
    else if (eventName === 'Product Sort Changed') { key = 'product_sort'; role = 'select'; label = 'Product sort'; action = 'change_sort'; }
    else if (eventName === 'Newsletter Signup Completed') { key = 'newsletter_form'; role = 'form'; label = 'Newsletter signup'; action = 'subscribe'; }
    else if (eventName === 'Product Inquiry Opened') { key = 'product_inquiry'; role = 'link'; label = 'Product inquiry'; action = 'open_inquiry'; }
    return { component_key: key, component_name: key, component_label: label,
      component_type: role === 'page' ? 'page' : 'control', component_role: role, placement, action };
  }

  function navigationElementKey(link) {
    return semanticLinkContext(link).component_key;
  }

  function navigationElementLabel(link) {
    return semanticLinkContext(link).component_label;
  }

  function ensureSession() {
    const now = Date.now();
    if (!state.sessionLoaded) loadSession();
    const expired = !state.sessionId || now - state.sessionLastActivityAt >= SESSION_TIMEOUT_MS;
    if (expired) {
      const resumeEngagement = state.sessionId && state.engagementStartedAt !== null;
      // Flush the elapsed interval under its original session before rotating.
      if (resumeEngagement) closeEngagementSegment('session_timeout');
      state.sessionId = createId('session');
      if (resumeEngagement && document.visibilityState !== 'hidden') beginEngagement();
    }
    state.sessionLastActivityAt = now;
    writeSession();
    return expired;
  }

  function loadSession() {
    state.sessionLoaded = true;
    try {
      if (!window.sessionStorage) return;
      const stored = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || 'null');
      if (!stored || typeof stored.id !== 'string') return;
      state.sessionId = stored.id;
      state.sessionLastActivityAt = Number(stored.lastActivityAt) || 0;
    } catch (_error) {
      state.sessionId = null;
      state.sessionLastActivityAt = 0;
    }
  }

  function writeSession() {
    try {
      if (!window.sessionStorage || !state.sessionId) return;
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        id: state.sessionId,
        lastActivityAt: state.sessionLastActivityAt,
      }));
    } catch (_error) {
      // A memory-only session is sufficient when storage is unavailable.
    }
  }

  function commonContext() {
    const context = Object.assign({}, state.navigation || {}, state.geography, {
      analytics_schema_version: 2, analytics_collector: 'theme_facade', price_unit: 'currency_major', page_view_id: state.pageViewId,
      page_path: safePagePath(),
      page_title: (document.title || config.page.title || '').trim(),
      page_type: config.page.type,
      page_url: safeCurrentUrl(),
      referrer: safeReferrer(),
      session_id: state.sessionId,
    });
    context.list_render_id = state.listRenderId;
    context.filter_state_id = state.filterStateId;
    if (pixelMode()) context.tab_id = state.tabId;
    if (state.catalog && (state.catalog.collection_handle || state.catalog.active_filters.length || state.catalog.brand)) {
      context.collection_handle = state.catalog.collection_handle;
      context.active_filters = state.catalog.active_filters;
      context.active_filter_count = state.catalog.active_filter_count;
      context.active_filters_truncated = state.catalog.active_filters_truncated;
      context._filterInputStatus = state.catalog.filterInputStatus;
      Object.assign(context, state.catalog.brand || {});
    }
    return context;
  }

  function behaviorTrackingAllowed() {
    return Boolean(
      config.compliance &&
      validToken(config.token) &&
      state.privacy === 'allowed' &&
      !state.excluded &&
      !state.identityFailed &&
      (!pixelMode() || state.sdk === 'ready')
    );
  }

  function safeCurrentUrl() {
    try {
      const url = new window.URL(window.location.href);
      return url.origin + url.pathname;
    } catch (_error) {
      return undefined;
    }
  }

  function safePagePath() {
    try {
      return window.location.pathname || new window.URL(window.location.href).pathname;
    } catch (_error) {
      return undefined;
    }
  }

  function safeReferrer() {
    try {
      if (!document.referrer) return undefined;
      const url = new window.URL(document.referrer);
      return url.origin + url.pathname;
    } catch (_error) {
      return undefined;
    }
  }

  function createId(prefix) {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return prefix + '-' + window.crypto.randomUUID();
      }
    } catch (_error) {
      // Fall back to time and randomness below.
    }
    return prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  function safeCall(target, method, args) {
    try {
      if (!target || typeof target[method] !== 'function') return false;
      target[method].apply(target, args || []);
      return true;
    } catch (_error) {
      // Vendor failures are deliberately isolated from storefront behavior.
      return false;
    }
  }

  function validToken(token) {
    return typeof token === 'string' && /^[A-Za-z0-9_-]{8,}$/.test(token);
  }

  function clampNumber(value, min, max, fallback) {
    const number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(max, Math.max(min, number));
  }
})();
